import { Suspense } from 'react';

import { getCookie } from '@api/HelperAPI';
import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { Spinner } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { FUNDING_STEP_ACTION } from '../../Funding.actions';
import { TFundingRequirementStatus } from '../deserialiseFundingRequirements';
import { FundingStepActionButton } from '../fundingStepActionButton/FundingStepActionButton';
import { TFundingRequirementsSteps } from '../orderFundingRequirements';
import StepItem from '../stepItem/StepItem';
import { BankDetailsActionDE } from './BankDetailsActionDE/BankDetailsActionDE';
import { PendingAccount } from './BankDetailsActionDE/PendingAccount';
import { FormalOffer } from '../../../myOffers/MyOffers.types';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    handleFundingStepAction: () => {
        dispatch({ type: FUNDING_STEP_ACTION, step: 'BANK_DETAILS' });
    }
});

const BankDetailsActionContents = ({
    step,
    handleFundingStepAction,
    formalOffer,
    triggerRefetchFundingRequirements
}: {
    step: TFundingRequirementsSteps[number];
    formalOffer: FormalOffer;
    triggerRefetchFundingRequirements: () => void;
} & ReturnType<typeof mapDispatchToProps>) => {
    const showNewDEBankAccountStepCookie =
        getCookie('showNewDEBankAccountStep') === 'true';

    const isDE = getCountry() === 'DE';

    const { branch: newAddFundingBankAccountTestBranch } = useSplitTestBranch({
        test: 'de_bank_details_funding_step_feb_2026',
        enabled: Boolean(
            !showNewDEBankAccountStepCookie &&
            isDE &&
            formalOffer.max_amount &&
            formalOffer.max_amount <= 3000
        ),
        suspense: true
    });
    const status = step.status as TFundingRequirementStatus;
    if (
        isDE &&
        (showNewDEBankAccountStepCookie ||
            newAddFundingBankAccountTestBranch === 'INCLUDE')
    ) {
        const errors = 'errors' in step ? step.errors : undefined;
        return (
            <BankDetailsActionDE
                status={status}
                errors={errors}
                maxFormalOfferAmount={formalOffer.max_amount}
                triggerRefetchFundingRequirements={
                    triggerRefetchFundingRequirements
                }
            />
        );
    }

    if (status === 'PENDING') {
        return <PendingAccount />;
    }
    return (
        <StepItem
            title={
                <FormattedMessage
                    id="_.funding_requirements.add_bank_details_title"
                    defaultMessage="Add your business bank details"
                />
            }
            caption={
                <FormattedMessage
                    id="_.funding_requirements.add_bank_details_caption"
                    defaultMessage="We’ll send the funds to this account."
                />
            }
            action={
                <FundingStepActionButton
                    actionMessage={
                        <FormattedMessage
                            id="_.funding_requirements.bank_details_action"
                            defaultMessage="Link Account"
                        />
                    }
                    pendingMessage={
                        <FormattedMessage
                            id="_.funding_requirements.bank_details_action_pending"
                            defaultMessage="Pending"
                        />
                    }
                    status={status}
                    handleFundingStepAction={handleFundingStepAction}
                    stepName="Bank details"
                />
            }
        />
    );
};

const BankDetailsAction = ({
    step,
    handleFundingStepAction,
    formalOffer,
    triggerRefetchFundingRequirements
}: {
    step: TFundingRequirementsSteps[number];
    formalOffer: FormalOffer;
    triggerRefetchFundingRequirements: () => void;
} & ReturnType<typeof mapDispatchToProps>) => {
    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense
                fallback={
                    <Spinner dataTestId="bank-details-action-container-loading" />
                }
            >
                <BankDetailsActionContents
                    step={step}
                    formalOffer={formalOffer}
                    handleFundingStepAction={handleFundingStepAction}
                    triggerRefetchFundingRequirements={
                        triggerRefetchFundingRequirements
                    }
                />
            </Suspense>
        </ErrorBoundary>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(BankDetailsAction);
